import React, { createContext, useContext, useMemo, useState, useEffect } from "react"
import { getCurrentUser, userHasPermission, userCanOperateAt, getAccessToken, TOKEN_KEY } from "src/services/authService"
import type { AuthenticatedUser, Permission } from "src/types/user"

interface UserContextValue {
  user: AuthenticatedUser | null
  hasPermission: (permission: Permission) => boolean
  canOperateAt: (facility: string) => boolean
}

const UserContext = createContext<UserContextValue>({
  user: null,
  hasPermission: () => false,
  canOperateAt: () => false,
})

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authToken, setAuthToken] = useState(() => getAccessToken())

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === null) {
        setAuthToken(getAccessToken())
      }
    }
    window.addEventListener("storage", handleStorage)
    return () => window.removeEventListener("storage", handleStorage)
  }, [])

  const value = useMemo<UserContextValue>(() => {
    const user = getCurrentUser()
    return {
      user,
      hasPermission: (permission) => userHasPermission(user, permission),
      canOperateAt: (facility) => userCanOperateAt(user, facility),
    }
  }, [authToken])
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export const useUser = () => useContext(UserContext)
